import React, { useEffect, useState } from "react";
import favicon from "../assets/fav-icon.png";

/**
 * LogoPreloader
 * A white-background website preloader that replaces the original dark
 * loading video. Each letter of the logo "scales in" (shrink → full size)
 * one after another, then the ring/mark spins gently while the whole
 * lockup holds. Set `loop` to true to repeat the reveal indefinitely,
 * or false to play once and settle.
 *
 * Usage:
 *   <LogoPreloader text="sec chive" loop={true} onFinish={() => setReady(true)} />
 */

const LETTER_DELAY = 90; // ms between each letter's entrance
const LETTER_DURATION = 520; // ms for a single letter's shrink-in animation

export default function LogoPreloader({
  text = "SINTX",
  loop = true,
  onFinish = () => {},
}) {
  const [cycle, setCycle] = useState(0);

  const letters = text.split("");
  const totalRevealTime = letters.length * LETTER_DELAY + LETTER_DURATION;

  useEffect(() => {
    if (!loop) {
      const t = setTimeout(onFinish, totalRevealTime + 600);
      return () => clearTimeout(t);
    }
    // Pause a beat after the full word is visible, then restart the reveal
    const restart = setTimeout(() => {
      setCycle((c) => c + 1);
    }, totalRevealTime + 900);
    return () => clearTimeout(restart);
  }, [cycle, loop]);

  return (
    <div style={styles.stage}>
      <style>{keyframes}</style>

      <div style={styles.word} role="img" aria-label={text}>
        {letters.map((ch, i) => (
          <span
            key={`${cycle}-${i}`}
            style={{
              ...styles.letter,
              animationDelay: `${i * LETTER_DELAY}ms`,
              color: letterColor(i, letters.length),
            }}
          >
            {ch === " " ? "\u00A0" : ch}
          </span>
        ))}
      </div>

    </div>
  );
}

// Soft gradient sweep across the wordmark, echoing the original magenta -> blue
function letterColor(index, total) {
  const stops = ["#c026d3", "#d946ef", "#a855f7", "#6366f1", "#3b82f6"];
  const t = total <= 1 ? 0 : index / (total - 1);
  const scaled = t * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(scaled));
  return stops[i + (scaled - i > 0.5 ? 1 : 0)];
}

const styles = {
  stage: {
    position: "fixed",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "28px",
    background: "#ffffff",
    zIndex: 9999,
  },
  mark: {
    position: "relative",
    width: 56,
    height: 56,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ring: {
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: "3px solid transparent",
    borderTopColor: "#d946ef",
    borderRightColor: "#6366f1",
    animation: "logo-spin 1.1s linear infinite",
  },
  logoImage: {
    width: "70%",
    height: "70%",
    objectFit: "contain",
    animation: "logo-pulse 1.6s ease-in-out infinite",
    borderRadius: "50%",
    zIndex: 1,
  },
  word: {
    display: "flex",
    fontFamily:
      "'Space Grotesk','Segoe UI',system-ui,-apple-system,sans-serif",
    fontSize: "clamp(28px, 5vw, 48px)",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  letter: {
    display: "inline-block",
    opacity: 0,
    transform: "scale(2.4)",
    filter: "blur(6px)",
    animationName: "letter-shrink-in",
    animationDuration: `${LETTER_DURATION}ms`,
    animationTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    animationFillMode: "forwards",
  },
  progressTrack: {
    width: 160,
    height: 3,
    borderRadius: 999,
    background: "#f1f0f4",
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    width: "40%",
    borderRadius: 999,
    background: "linear-gradient(90deg, #d946ef, #6366f1)",
    animation: "progress-sweep 1.3s ease-in-out infinite",
  },
};

const keyframes = `
@keyframes letter-shrink-in {
  0% {
    opacity: 0;
    transform: scale(2.4);
    filter: blur(6px);
  }
  60% {
    opacity: 1;
    transform: scale(0.92);
    filter: blur(0px);
  }
  100% {
    opacity: 1;
    transform: scale(1);
    filter: blur(0px);
  }
}
@keyframes logo-spin {
  to { transform: rotate(360deg); }
}
@keyframes logo-pulse {
  0%, 100% { opacity: 0.35; transform: scale(0.9); }
  50% { opacity: 0.65; transform: scale(1); }
}
@keyframes progress-sweep {
  0% { transform: translateX(-120%); }
  100% { transform: translateX(340%); }
}
@media (prefers-reduced-motion: reduce) {
  * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; }
}
`;